import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import * as Sentry from '@sentry/react';

import { getUser } from '@frontend/atoms/opalUserAtom';
import { getOrRenewOptiIdToken } from '@frontend/oktaAuth';
import { AGENT_TEST_RUN_INSTANCE_OVERRIDE_KEY } from '@frontend/utils/misc';

import { sendSentryError } from '../utils/sendSentryError';

const MAX_RETRIES = 2;

type RetryableAxiosRequestConfig = {
  _retryCount?: number;
} & InternalAxiosRequestConfig;

// Auth strategy for the first-party app, mirroring the embedded chat toggle
// (localStorage['ai-chat:AUTH_STRATEGY']). Under the bearer strategy the app
// authenticates cookielessly — the OptiID bearer is still sent, but no cookies
// are attached (withCredentials:false). The cookie strategy is unchanged
// (bearer + credentials): the gateway still bootstraps the session cookie by
// exchanging the bearer.
function isBearerStrategy(): boolean {
  try {
    return localStorage.getItem('ai-chat:AUTH_STRATEGY') === 'bearer';
  } catch {
    return false;
  }
}

const sentryRequestIdInterceptor = (requestId: string | null | undefined): void => {
  try {
    if (requestId) {
      Sentry.setTag('requestId', requestId);
      Sentry.setContext('request', {
        requestId: requestId,
      });
    }
  } catch (err) {
    console.warn('Failed to process request ID for Sentry:', err);
  }
};

class HttpClient {
  private axiosInstance: AxiosInstance;

  constructor(baseURL?: string, withAuth: boolean = false) {
    this.axiosInstance = axios.create({
      baseURL,
      withCredentials: true,
      // Default request timeout for all HTTP calls. Slow endpoints (agent
      // execution, tool discovery, provisioning, etc.) override this per-request.
      timeout: 60000,
    });

    this.setupInterceptors(withAuth);
  }

  private setupInterceptors(withAuth: boolean): void {
    if (withAuth) {
      this.axiosInstance.interceptors.request.use(async config => {
        config.headers = config.headers || {};
        const token = await getOrRenewOptiIdToken();
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      });
    }

    this.axiosInstance.interceptors.request.use(config => {
      config.headers = config.headers || {};
      // Bearer strategy is cookieless — never send the session cookie. Cookie
      // strategy keeps the default (credentials on) so the gateway can bootstrap
      // and refresh the session cookie.
      if (isBearerStrategy()) {
        config.withCredentials = false;
        config.headers['X-Opal-Auth-Strategy'] = 'bearer';
      }
      config.headers['X-Opal-Client-Type'] = 'web';
      const user = getUser();

      // Check if we should override the instance ID for agent test runs
      const overrideInstanceId = localStorage.getItem(AGENT_TEST_RUN_INSTANCE_OVERRIDE_KEY);
      const agentExecutionRoutePattern =
        /\/agents\/(specialized\/[^/]+\/execute|workflows\/[^/]+\/trigger)/;
      const isAgentExecutionRoute = config.url && agentExecutionRoutePattern.test(config.url);

      if (overrideInstanceId && isAgentExecutionRoute) {
        config.headers['X-Instance-Id'] = overrideInstanceId;
      } else if (user?.instance?.instance_id) {
        config.headers['X-Instance-Id'] = user.instance.instance_id;
      }
      // X-Opal-Region drives Global LB region routing. It is sourced from the
      // per-tab Jotai atom (not a cookie) so two tabs on different regions
      // each route to their own backend independently. Defaults to 'US' when
      // unknown so legacy/region-unaware traffic keeps landing on US.
      if (user?.instance) {
        config.headers['X-Opal-Region'] = user.instance.hosted_region || 'US';
      }

      return config;
    });

    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        const requestId = response?.headers['x-request-id'];
        sentryRequestIdInterceptor(requestId);
        return response;
      },
      (error: AxiosError) => {
        const config = error.config as RetryableAxiosRequestConfig | undefined;
        const { response } = error;
        const status = response?.status;

        // Only retry on 429 (Too Many Requests), not on 401, 403, or other 4xx errors
        if (config && status === 429 && (!config._retryCount || config._retryCount < MAX_RETRIES)) {
          config._retryCount = config._retryCount ? config._retryCount + 1 : 1;
          return this.axiosInstance(config);
        }
        const isNetworkError = error.code === AxiosError.ERR_NETWORK;
        const isExpectedClientError = status === 400 || status === 401 || status === 403;

        if (!isNetworkError && !isExpectedClientError) {
          const requestId = response?.headers?.['x-request-id'];
          sentryRequestIdInterceptor(requestId);

          const errorContext = {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
          };
          sendSentryError(error, errorContext);
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.get<T>(url, config);
  }

  async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.post<T>(url, data, config);
  }

  async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.put<T>(url, data, config);
  }

  async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.patch<T>(url, data, config);
  }

  async delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.delete<T>(url, config);
  }

  getInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

/**
 * Extracts a meaningful error message from API responses, particularly AxiosError instances.
 *
 * @param error - The error object to extract a message from
 * @returns A human-readable error message
 *
 * @example
 * ```typescript
 * try {
 *   await api.post('/endpoint', data);
 * } catch (error) {
 *   const message = extractApiErrorMessage(error);
 *   throw new Error(message);
 * }
 * ```
 */
export function extractApiErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    // Check for detailed message in response.data.detail.message
    const detailMessage = error.response?.data?.detail?.message;
    if (detailMessage && typeof detailMessage === 'string') {
      return detailMessage;
    }

    // Check if response.data.detail is an array and extract the first message
    const detail = error.response?.data?.detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const firstError = detail[0];
      if (firstError?.msg && typeof firstError.msg === 'string') {
        return firstError.msg;
      }
    }

    // Fallback to response.data.detail if it's a string
    if (detail && typeof detail === 'string') {
      return detail;
    }

    // Fallback to response.data.error (used by TMS registration endpoints)
    const errorField = error.response?.data?.error;
    if (errorField && typeof errorField === 'string') {
      return errorField;
    }

    // Fallback to response.data.message
    const message = error.response?.data?.message;
    if (message && typeof message === 'string') {
      return message;
    }
  }

  // Generic fallback
  return 'An error occurred while processing your request';
}

/**
 * Extracts the error code from an API error response.
 * Useful for conditional error handling based on specific error types.
 *
 * @param error - The error object from an API call
 * @returns The error code string if present, null otherwise
 *
 * @example
 * ```typescript
 * const errorCode = extractApiErrorCode(error);
 * if (errorCode === 'SCOPE_BUNDLE_NAME_EXISTS') {
 *   // Handle duplicate name error specifically
 * }
 * ```
 */
export function extractApiErrorCode(error: unknown): string | null {
  if (error instanceof AxiosError) {
    return error.response?.data?.detail?.code ?? null;
  }
  return null;
}

const authenticatedHttpClient = new HttpClient(undefined, true);
export default authenticatedHttpClient;
